import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  CreateParagraphStrict,
  DeleteParagraphStrict,
  EditParagraphStrict,
  GetParagraphById,
} from "../../services/paragraphs";
import { EditHeadingStrict } from "../../services/headings";
import {
  createHighlight,
  deleteHighlight,
  updateHighlight,
} from "../../services/transcriptionHighlights";

/**
 * Undo for the transcript editor.
 *
 * Every editing action snapshots the rows it is about to touch, and undo
 * writes those rows back. One mechanism covers text edits, speaker rename
 * and swap, split, merge and delete — there is no per-operation inverse to
 * maintain.
 *
 * Deleted paragraphs are recreated with their ORIGINAL id. Postgres never
 * reuses a sequence value and POST /paragraph/ passes the body straight to
 * the model, so the id survives and anything pointing at it stays valid.
 *
 * The stack is per browser tab and is not persisted: reloading the page
 * clears it, the same as closing a document in Word.
 */

export const UNDO_LIMIT = 30;

// Fields an undo entry carries for a paragraph. Anything outside this list
// is server-owned (created_at, updated_at) or derived, and is not restored.
const RESTORABLE_FIELDS = [
  "corrected_content",
  "original_content",
  "raw_content",
  "start",
  "end",
  "speaker_name",
  "speaker_number",
  "locale",
  "transcription_id",
];

/**
 * Read paragraphs straight from the server so the snapshot holds canonical
 * values.
 *
 * The session view normalises `start` to seconds as a NUMBER (4208.64) while
 * the column holds an ISO duration ('PT1H10M8.64S'). Snapshotting a view
 * object and writing it back would replace the timestamp with a number.
 * Anything recording an undo entry from view state must use this instead.
 *
 * Rows that no longer exist come back as null and are dropped.
 */
export const fetchSnapshots = async (ids) => {
  const rows = await Promise.all(
    (ids || []).map(async (id) => {
      try {
        return await GetParagraphById(id);
      } catch (e) {
        return null;
      }
    })
  );
  return rows.filter(Boolean);
};

/** Reduce a paragraph to the fields undo restores, plus its id. */
export const snapshotParagraph = (p) => {
  if (!p || p.id == null) return null;
  const out = { id: p.id };
  RESTORABLE_FIELDS.forEach((f) => {
    if (p[f] !== undefined) out[f] = p[f];
  });
  return out;
};

/**
 * What we expect to find on the server at undo time. Undo compares this
 * against the live row and refuses if it moved — otherwise it would clobber
 * a later change, whether from another user or from the AI correction pass.
 */
const fingerprint = (p) =>
  p == null
    ? null
    : [
        p.corrected_content ?? "",
        p.original_content ?? "",
        p.speaker_name ?? "",
        p.speaker_number ?? "",
      ].join("\u0000");

const slice = createSlice({
  name: "undo",
  initialState: {
    entries: [],
    busy: false,
    lastError: null,
    syncToken: 0,
    // null means "re-read from props"; otherwise the rows to apply directly
    syncRows: null,
    syncIds: null,
  },
  reducers: {
    /**
     * Record one undoable action.
     *
     * restore  — rows that still exist and should be written back (PUT)
     * recreate — rows that were deleted and should come back (POST, same id)
     * remove   — rows that were created and should go away (DELETE)
     * expect   — { [id]: fingerprint } captured AFTER the action, for the guard
     */
    pushUndo(state, action) {
      const { type, label, sessionId } = action.payload;
      state.entries.push({
        type,
        label,
        sessionId,
        restore: action.payload.restore || [],
        restoreHeadings: action.payload.restoreHeadings || [],
        // Highlights live in their own table and are referenced by nothing,
        // so a restored one may take a new id — unlike paragraphs, whose ids
        // must survive because headings point at them.
        restoreHighlights: action.payload.restoreHighlights || [],
        recreateHighlights: action.payload.recreateHighlights || [],
        removeHighlights: action.payload.removeHighlights || [],
        recreate: action.payload.recreate || [],
        remove: action.payload.remove || [],
        expect: action.payload.expect || {},
        // "content" (default): refuse if the row changed since the action.
        // "existence": only check the row is there and not mid-correction —
        // used by split, whose AI regeneration always rewrites the text, so a
        // content check would refuse every undo a few seconds after the split.
        guard: action.payload.guard || "content",
        at: Date.now(),
      });
      if (state.entries.length > UNDO_LIMIT) state.entries.shift();
    },
    clearUndo(state) {
      state.entries = [];
      state.lastError = null;
    },
    dismissUndoError(state) {
      state.lastError = null;
    },
    /**
     * Force every paragraph view to re-read its content from props.
     *
     * A textarea holds locally-edited text that its props never carried —
     * props still hold the pre-edit value. Undo returns the server to that
     * same value, so nothing in props changes and the normal sync effect
     * never fires. Bumping this after the refetch tells the views to resync
     * unconditionally. Dispatch it AFTER the refetch, or they resync from
     * stale props.
     */
    /**
     * Hand the restored text to the paragraph views.
     *
     * Payload: [{ id, original_content, corrected_content }] — or null to
     * mean "re-read from props" (used after a background refetch).
     *
     * Telling views to re-read props does not work on its own: the restored
     * rows reach them through a cascade (store -> regroup -> local state ->
     * props) that has not finished when this fires, so they would resync
     * from stale props and mark themselves done. The content travels with
     * the signal instead, so it cannot be raced.
     */
    bumpUndoSync(state, action) {
      state.syncToken += 1;
      const payload = action?.payload || {};
      // rows: apply this content directly (used immediately after the undo,
      //       before the restored rows have propagated down as props).
      // ids:  these paragraphs should re-read from props (used after the
      //       refetch, when props ARE authoritative). Limiting it to the
      //       touched ids keeps ~200 components from resyncing at once.
      state.syncRows = Array.isArray(payload.rows) ? payload.rows : null;
      state.syncIds = Array.isArray(payload.ids) ? payload.ids.map(Number) : null;
    },
  },
  extraReducers: (b) => {
    b.addCase(undoLastAsync.pending, (s) => {
      s.busy = true;
      s.lastError = null;
    })
      .addCase(undoLastAsync.fulfilled, (s) => {
        s.busy = false;
        s.entries.pop();
      })
      .addCase(undoLastAsync.rejected, (s, a) => {
        s.busy = false;
        s.lastError = a.error?.message || "Rückgängig fehlgeschlagen.";
      });
  },
});

/**
 * Apply the inverse of the newest entry.
 *
 * Guard first, write second: if any affected row changed since the action, or
 * is mid AI-correction, nothing is written and the entry stays on the stack.
 */
export const undoLastAsync = createAsyncThunk(
  "undo/undoLastAsync",
  async (_, { getState }) => {
    const entry = getState().undo.entries.slice(-1)[0];
    if (!entry) throw new Error("Nichts rückgängig zu machen.");

    // ── guard ────────────────────────────────────────────────────────────
    // Read every affected row at once. Sequentially this was one network
    // round trip per paragraph, which a merge of several makes obvious.
    const expectEntries = Object.entries(entry.expect || {});
    const liveRows = await Promise.all(
      expectEntries.map(async ([idStr]) => {
        try {
          return await GetParagraphById(Number(idStr));
        } catch (e) {
          return null;
        }
      })
    );

    for (let i = 0; i < expectEntries.length; i += 1) {
      const [idStr, expected] = expectEntries[i];
      const id = Number(idStr);
      const live = liveRows[i];
      const exists = live && live.id != null;

      if (entry.recreate.some((r) => r.id === id)) {
        // Should still be absent. If it came back, someone else restored it.
        if (exists) {
          throw new Error(
            "Dieser Absatz wurde inzwischen wiederhergestellt. Rückgängig abgebrochen."
          );
        }
        continue;
      }
      if (!exists) {
        throw new Error(
          "Ein betroffener Absatz existiert nicht mehr. Rückgängig abgebrochen."
        );
      }
      if (live.correction_status === "pending") {
        throw new Error(
          "Die KI-Korrektur läuft noch für diesen Absatz. Bitte kurz warten."
        );
      }
      if (entry.guard === "content" && fingerprint(live) !== expected) {
        throw new Error(
          "Dieser Absatz wurde zwischenzeitlich geändert. Rückgängig abgebrochen."
        );
      }
    }

    // ── write ────────────────────────────────────────────────────────────
    // Groups run in order (remove, then recreate, then restore) but the rows
    // within a group are independent, so they go out together.
    await Promise.all(entry.remove.map((id) => DeleteParagraphStrict(id)));
    // id travels in the body and is preserved
    await Promise.all(entry.recreate.map((row) => CreateParagraphStrict(row)));
    await Promise.all(
      entry.restore.map(({ id, ...payload }) => EditParagraphStrict(payload, id))
    );
    // Merge re-anchors headings that pointed at a deleted paragraph onto the
    // surviving one. Undo puts them back, otherwise the heading silently
    // drifts to a different position in the transcript.
    await Promise.all(
      (entry.restoreHeadings || []).map(({ id, ...payload }) =>
        EditHeadingStrict(id, payload)
      )
    );
    for (const id of entry.removeHighlights || []) {
      await deleteHighlight(id);
    }
    for (const h of entry.recreateHighlights || []) {
      const { id, created_at, updated_at, ...payload } = h;
      await createHighlight(payload); // id is not preserved; nothing refers to it
    }
    for (const h of entry.restoreHighlights || []) {
      const { id, color, note, snippet } = h;
      await updateHighlight(id, { color, note, snippet });
    }

    // Read the affected rows back instead of assuming the writes produced
    // exactly what was sent. Trusting the snapshot let the two panels
    // disagree — the left reads raw_content from props while the right holds
    // corrected_content in local state, so any divergence between what we
    // think we wrote and what the row actually holds shows up as one panel
    // being stale until a reload. These are the values the UI will display.
    const touchedIds = [
      ...(entry.restore || []).map((r) => r.id),
      ...(entry.recreate || []).map((r) => r.id),
    ];
    const applied = await fetchSnapshots(touchedIds);
    return { entry, applied };
  }
);

export const { pushUndo, clearUndo, dismissUndoError, bumpUndoSync } =
  slice.actions;
export const selectUndoSyncToken = (s) => s.undo.syncToken;
export const selectUndoSyncRows = (s) => s.undo.syncRows;
export const selectUndoSyncIds = (s) => s.undo.syncIds;
export const selectCanUndo = (s) => s.undo.entries.length > 0 && !s.undo.busy;
export const selectUndoDepth = (s) => s.undo.entries.length;
export const selectUndoLabel = (s) =>
  s.undo.entries.length ? s.undo.entries[s.undo.entries.length - 1].label : null;
export const selectUndoBusy = (s) => s.undo.busy;
export const selectUndoError = (s) => s.undo.lastError;

/**
 * Build an undo entry from before/after paragraph arrays.
 * Rows present before but not after were deleted; the reverse were created.
 */
export const buildUndoEntry = ({
  type,
  label,
  sessionId,
  before,
  after,
  headingsBefore,
  guard,
  restoreHighlights,
  recreateHighlights,
  removeHighlights,
}) => {
  const beforeRows = (before || []).map(snapshotParagraph).filter(Boolean);
  const afterRows = (after || []).map(snapshotParagraph).filter(Boolean);
  const afterById = new Map(afterRows.map((r) => [r.id, r]));
  const beforeIds = new Set(beforeRows.map((r) => r.id));

  const expect = {};
  (after || []).forEach((p) => {
    if (p && p.id != null) expect[p.id] = fingerprint(p);
  });
  const recreate = beforeRows.filter((r) => !afterById.has(r.id));
  recreate.forEach((r) => {
    expect[r.id] = null; // must be absent at undo time
  });

  return {
    type,
    label,
    sessionId,
    guard,
    restoreHighlights,
    recreateHighlights,
    removeHighlights,
    restoreHeadings: (headingsBefore || [])
      .filter((h) => h && h.id != null)
      .map((h) => ({ id: h.id, before_paragraph_id: h.before_paragraph_id })),
    restore: beforeRows.filter((r) => afterById.has(r.id)),
    recreate,
    remove: afterRows.filter((r) => !beforeIds.has(r.id)).map((r) => r.id),
    expect,
  };
};

export default slice.reducer;
